/**
 * Specification-driven development progress.
 *
 * Agents report which documents they read and write — SPEC.md, DESIGN.md, ARCHITECTURE.md and
 * whatever else the organization's filename rules classify — and the backend groups that activity
 * into phases. This screen shows how far each phase has got.
 *
 * The number needs care. Everywhere else in DevPulse a percentage is computed from structured work
 * steps and an agent cannot set it (ADR-0007). Here it genuinely is the agent's own claim about a
 * document it read, because nothing else could know. That difference is stated on the screen, not
 * buried in a tooltip: a figure whose provenance is different from every other figure in the
 * product has to say so, or the two get read the same way.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FileText, Info } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { formatRelative } from '../lib/format'
import { EmptyState, Panel, ProgressBar, Skeleton } from '../components/primitives'

/** The order phases are shown in, which is the order they happen in. */
const PHASE_ORDER = [
  'Requirements',
  'Specification',
  'Design',
  'Architecture',
  'Implementation',
  'Testing',
  'Tasks',
  'Documentation',
]

const PHASE_KEY: Record<string, string> = {
  Requirements: 'sdd.requirements',
  Specification: 'sdd.specification',
  Design: 'sdd.design',
  Architecture: 'sdd.architecture',
  Implementation: 'sdd.implementation',
  Testing: 'sdd.testing',
  Tasks: 'sdd.tasks',
  Documentation: 'sdd.documentation',
}

export default function Sdd() {
  const { t, locale } = useI18n()
  const [taskId, setTaskId] = useState('')

  const { data: tasks } = useQuery({
    queryKey: ['tasks', 'for-sdd'],
    queryFn: () => api.tasks.list({ pageSize: 100 }),
  })

  const { data, isLoading } = useQuery({
    queryKey: ['sdd', taskId],
    queryFn: () => api.sessions.sdd(taskId || undefined),
  })

  const phases = [...(data?.phases ?? [])].sort(
    (a, b) => PHASE_ORDER.indexOf(a.category) - PHASE_ORDER.indexOf(b.category),
  )

  const totalDocuments = phases.reduce((sum, phase) => sum + phase.documentCount, 0)

  return (
    <div className="stack stack--loose">
      <div className="row row--wrap">
        {data?.taskExternalId && (
          <span className="tiny muted">
            <span className="mono">{data.taskExternalId}</span> · {data.taskTitle}
          </span>
        )}

        <div className="spacer" />

        <label className="field" style={{ minWidth: 260 }}>
          <span className="field__label">{t('tasks.title')}</span>
          <select className="select" value={taskId} onChange={(event) => setTaskId(event.target.value)}>
            <option value="">{t('sdd.allTasks')}</option>
            {(tasks?.items ?? []).map((task) => (
              <option key={task.id} value={task.id}>
                {task.externalId} — {task.title}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/*
        Stated once, at the top, before any number is read. The completeness below is the agent's
        own assessment of a document; it is not computed from work steps the way session progress
        is, and presenting the two identically without saying so would be the exact failure §4 of
        the brief warns about.
      */}
      <div className="row row--tight tiny muted">
        <Info size={13} />
        <span>{t('sdd.agentClaim')}</span>
      </div>

      {isLoading ? (
        <div className="grid grid--two">
          <Skeleton height={180} />
          <Skeleton height={180} />
        </div>
      ) : totalDocuments === 0 ? (
        <Panel>
          <EmptyState title={t('sdd.empty')} body={t('sdd.emptyHint')} />
        </Panel>
      ) : (
        <div className="grid grid--two">
          {phases.map((phase) => (
            <Panel
              key={phase.category}
              title={t(PHASE_KEY[phase.category] ?? phase.category)}
              flush
            >
              <div className="stack" style={{ padding: 'var(--s4)' }}>
                <ProgressBar
                  value={phase.completenessPercent}
                  showValue
                  size="lg"
                  tone={phase.completenessPercent >= 100 ? 'ok' : undefined}
                />

                <div className="row row--between tiny muted">
                  <span>{t('sdd.documents', { count: phase.documentCount })}</span>
                  <span>
                    {phase.updateCount} {t('sdd.updates')}
                  </span>
                </div>

                {phase.lastUpdatedAt && (
                  <span className="tiny muted">
                    {t('sdd.lastUpdated')}: {formatRelative(phase.lastUpdatedAt, locale)}
                  </span>
                )}

                {phase.documents.length === 0 ? (
                  <span className="tiny muted">{t('sdd.noDocuments')}</span>
                ) : (
                  <ul className="sdd__docs">
                    {phase.documents.map((document) => (
                      <li key={document} className="row row--tight tiny">
                        <FileText size={12} className="muted" />
                        {/* A path, never contents. The protocol has no field for contents. */}
                        <span className="mono truncate">{document}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Panel>
          ))}
        </div>
      )}
    </div>
  )
}
